import { Device, Zone } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import useTheme from "@mui/material/styles/useTheme";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { endOfDay } from "date-fns/endOfDay";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import StaysGrid from "./components/StaysGrid";
import ZoneLogNotices from "./components/ZoneLogNotices";
import ZoneLogSetupDialog from "./components/ZoneLogSetupDialog";
import ZoneVisitMapDrawer from "./components/ZoneVisitMapDrawer";
import { StayRow } from "./models/StayRow";
import { toStayRows } from "./utils/stayRows";
import { hasFullDataScope, lateRecordingRules, planZoneVisitSync, zoneTypeIdOfRule } from "./utils/zoneLogRules";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { useGroupDeviceIds } from "@/hooks/devices/useGroupDeviceIds";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";
import { useAllZones } from "@/hooks/zone/useAllZones";
import { useZoneTypes } from "@/hooks/zone/useZoneTypes";
import { useSyncZoneLogRules } from "@/hooks/zoneLog/useSyncZoneLogRules";
import { useZoneLogReprocessed } from "@/hooks/zoneLog/useZoneLogReprocessed";
import { useZoneLogSetup } from "@/hooks/zoneLog/useZoneLogSetup";
import { useZoneStays } from "@/hooks/zoneLog/useZoneStays";
import { devicesActiveDuring } from "@/pages/energy-consumption-report/models/devicesActiveDuring";
import { zoneTypeId } from "@/pages/zones/utils/zoneTypeId";
import { normalizeZoneTypes, withBuiltInZoneTypes, zoneTypeLabel } from "@/pages/zones/utils/zoneTypes";

const COMPANY_GROUP_ID = "GroupCompanyId";

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

export default function ZoneReportPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));
  const { groups } = useAddInState();
  const { me } = useGeotabClient();

  const setupQuery = useZoneLogSetup();
  const zonesQuery = useAllZones();
  const typesQuery = useZoneTypes();
  const sync = useSyncZoneLogRules();
  const { fromDate, toDate, setRange, isReady } = usePersistedDateRange({
    key: "dateRange:zoneReport",
    defaultLookbackDays: 7,
  });

  const [selected, setSelected] = useState<Device[]>([]);
  const [dialogDismissed, setDialogDismissed] = useState(false);
  const [mapRow, setMapRow] = useState<StayRow | null>(null);

  const setup = setupQuery.data;
  const zones = useMemo(() => zonesQuery.data ?? [], [zonesQuery.data]);
  const rules = useMemo(() => setup?.rules ?? [], [setup?.rules]);
  const types = useMemo(() => withBuiltInZoneTypes(normalizeZoneTypes(typesQuery.data ?? [])), [typesQuery.data]);
  const typeNames = useMemo(() => new Map(types.map(type => [type.id, zoneTypeLabel(type, t)])), [types, t]);
  const plan = useMemo(
    () => planZoneVisitSync(zones, [...typeNames].map(([id, name]) => ({ id, name })), rules),
    [zones, typeNames, rules],
  );
  const reprocessed = useZoneLogReprocessed(rules);
  const noTrackedZones = !zones.some(zone => zone.mustIdentifyStops && (zone.zoneTypes ?? []).length > 0);

  // The page's group filter narrows the vehicles further; the whole company needs no lookup.
  const groupFiltered = !!groups?.some(group => group.id !== COMPANY_GROUP_ID);
  const groupDeviceIds = useGroupDeviceIds({ enabled: groupFiltered });
  const available = useMemo(() => {
    // Archived vehicles stay pickable for periods they were active in.
    const devices = devicesActiveDuring(setup?.devices ?? [], fromDate);
    if (!groupFiltered || !groupDeviceIds.data) return devices;
    const inGroup = new Set(groupDeviceIds.data);
    return devices.filter(device => inGroup.has(device.id));
  }, [setup?.devices, fromDate, groupFiltered, groupDeviceIds.data]);
  const deviceIds = useMemo(
    () => (selected.length > 0 ? selected : available).map(device => device.id).sort(),
    [selected, available],
  );
  const ruleIds = useMemo(() => rules.map(rule => rule.id).sort(), [rules]);

  const stays = useZoneStays({
    ruleIds,
    deviceIds,
    from: fromDate,
    to: toDate,
    groupId: setup?.zonelogsGroup?.id,
    enabled: isReady && !!setup && (!groupFiltered || !!groupDeviceIds.data),
  });

  const rows = useMemo(() => {
    const typeLabel = (zone: Zone) =>
      (zone.zoneTypes ?? [])
        .map(ref => {
          const id = zoneTypeId(ref);
          return id ? (typeNames.get(id) ?? id) : undefined;
        })
        .filter(Boolean)
        .join(", ");
    const ruleType = new Map<string, string>();
    for (const rule of rules) {
      const typeId = zoneTypeIdOfRule(rule);
      if (typeId) ruleType.set(rule.id, typeId);
    }
    return toStayRows(stays.events, {
      ruleType,
      visitZones: stays.visitZones ?? new Map(),
      zones: new Map(zones.map(zone => [zone.id, zone])),
      typeNames,
      deviceNames: new Map((setup?.devices ?? []).map(device => [device.id, device.name])),
      typeLabel,
      from: fromDate,
      to: toDate,
      now: new Date(),
      lastData: stays.lastData,
    });
  }, [stays.events, stays.visitZones, stays.lastData, rules, zones, typeNames, setup?.devices, fromDate, toDate]);

  const lateZones = useMemo(() => {
    const earliestStayByRule = new Map<string, Date>();
    for (const event of stays.events) {
      const ruleId = (event.rule as { id?: string } | undefined)?.id;
      const start = new Date(event.activeFrom);
      if (!ruleId || Number.isNaN(start.getTime())) continue;
      const known = earliestStayByRule.get(ruleId);
      if (!known || start < known) earliestStayByRule.set(ruleId, start);
    }
    // Until it is known which rules were reprocessed, no type is named: a wrong notice is worse than a late one.
    if (!reprocessed.data) return [];
    return lateRecordingRules(rules, earliestStayByRule, reprocessed.data, fromDate, typeNames);
  }, [stays.events, rules, fromDate, reprocessed.data, typeNames]);

  // Without the whole database in view, existing rules may be hidden and would be created twice.
  const canSetUp = hasFullDataScope(me?.companyGroups);
  const needsSetup = plan.missing.length > 0;
  const dialogOpen = !dialogDismissed && canSetUp && !!setup?.servicesGroup && zonesQuery.isSuccess && typesQuery.isSuccess && needsSetup;

  const handleCreate = () => {
    if (!setup?.servicesGroup) return;
    sync.mutate(
      { servicesGroupId: setup.servicesGroup.id, zonelogsGroupId: setup.zonelogsGroup?.id, types: plan.missing },
      {
        onSuccess: ({ created }) => {
          enqueueSnackbar(t("zoneReportPage.setup.created", { count: created }), { variant: "success" });
          setDialogDismissed(true);
        },
        onError: error =>
          enqueueSnackbar(t("zoneReportPage.setup.failed", { message: errorMessage(error) }), { variant: "error" }),
      },
    );
  };

  const setupLoaded = setupQuery.isSuccess && zonesQuery.isSuccess;

  return (
    <>
      <FilterStackLayout
        filterMaxWidth={1512}
        isMobile={isMobile}
        filters={
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              width: "100%",
              gap: 2,
            }}
          >
            <Autocomplete
              multiple
              size="small"
              sx={{ flex: 1, minWidth: 240, width: "100%" }}
              options={available}
              value={selected}
              onChange={(_, value) => setSelected(value)}
              getOptionLabel={device => device.name ?? device.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={params => <TextField {...params} label={t("zoneReportPage.vehicles")} />}
            />
            <DateRangeSelect
              fromDate={fromDate}
              toDate={toDate}
              onChange={(from, to) => setRange(from, to ?? (from ? endOfDay(from) : undefined))}
            />
          </Box>
        }
        content={
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, gap: 1 }}>
            {setupQuery.isError && (
              <Alert severity="error">
                {t("zoneReportPage.setupLoadFailed", { message: errorMessage(setupQuery.error) })}
              </Alert>
            )}
            {setupLoaded && (
              <ZoneLogNotices
                servicesMissing={!setup?.servicesGroup}
                zonelogsEmpty={!!setup?.servicesGroup && (!setup.zonelogsGroup || setup.devices.length === 0)}
                noTrackedZones={zonesQuery.isSuccess && noTrackedZones}
                setupNeedsAdmin={needsSetup && !canSetUp && !!me}
                lateZones={lateZones}
              />
            )}
            {stays.error && (
              <Alert severity="error">{t("zoneReportPage.loadFailed", { message: errorMessage(stays.error) })}</Alert>
            )}
            {!stays.complete && <Alert severity="warning">{t("zoneReportPage.incomplete")}</Alert>}
            {stays.progress && (
              <Box>
                <LinearProgress
                  variant={stays.progress.total ? "determinate" : "indeterminate"}
                  value={stays.progress.total ? (stays.progress.done / stays.progress.total) * 100 : undefined}
                />
                <Typography variant="caption" color="text.secondary">
                  {stays.progress.total
                    ? t("zoneReportPage.progress", stays.progress)
                    : t("zoneReportPage.progressPages", stays.progress)}
                </Typography>
              </Box>
            )}
            <StaysGrid
              rows={rows}
              loading={!setupLoaded || stays.isLoading}
              fromDate={fromDate}
              toDate={toDate}
              onShowMap={setMapRow}
            />
            {stays.stats && (
              <Typography variant="caption" color="text.secondary">
                {t("zoneReportPage.stats", {
                  ...stays.stats,
                  plan: t(`zoneReportPage.plans.${stays.stats.plan}`),
                  rules: ruleIds.length,
                  seconds: (stays.stats.ms / 1000).toFixed(1),
                })}
              </Typography>
            )}
          </Box>
        }
      />
      <ZoneVisitMapDrawer row={mapRow} zone={zones.find(zone => zone.id === mapRow?.zoneId)} onClose={() => setMapRow(null)} />
      <ZoneLogSetupDialog
        open={dialogOpen}
        typeCount={plan.missing.length}
        createsGroup={!setup?.zonelogsGroup}
        busy={sync.isPending}
        progress={sync.progress}
        onCreate={handleCreate}
        onLater={() => setDialogDismissed(true)}
      />
    </>
  );
}
